import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';

import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import { ROUTES } from '../../../../shared/constants/routes';
import { CONFIG } from '../../../../shared/constants/common';
import { isUsCountry, getPendingSigningUrl } from '../../../../shared/utils/function';
import { isW8FormExpired, shouldShowTaxInfoInAccountSetup } from '../../../../shared/utils/earningsPayoutUtil';
import AddTaxInfoCard from './AddTaxInfoCard';
import TaxBanditsSignatureRequiredCard from '../TaxInfo/Card/TaxBanditsSignatureRequiredCard';
import BalanceOverviewCard from './BalanceOverviewCard';
import TotalEarningsTodayCard from './TotalEarningsTodayCard';
import EarningsBreakdownCard from './EarningsBreakdownCard';
import MonthlyEarningsCard from './MonthlyEarningsCard';
import DailyEarningsCard from './DailyEarningsCard';
import PayoutHistoryCard from './PayoutHistoryCard';
import AccountSetupCard from './AccountSetupCard';
import ExportDataCard from './ExportDataCard';
import { getBalanceSummary, getW8Form, getW9Form } from '../../../../shared/actions/transactionAction';
import { getVatRateByCountryCode } from '../../../../shared/constants/vatRates';
import { getGstRateByCountryCode } from '../../../../shared/constants/gstCountry';
import { reportError } from '../../../../shared/sentry';

const EarningsPayout = ({
  user
}) => {
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [w8FormFilled, setW8FormFilled] = useState(false);
  const [w8FormExpired, setW8FormExpired] = useState(false);
  const [w8PendingSigningUrl, setW8PendingSigningUrl] = useState(null);
  const [w8StatusLoading, setW8StatusLoading] = useState(false);
  const [w9PendingSigningUrl, setW9PendingSigningUrl] = useState(null);
  const [w9FormFilled, setW9FormFilled] = useState(false);
  const [w9StatusLoading, setW9StatusLoading] = useState(false);
  const [payoutDataRefreshKey, setPayoutDataRefreshKey] = useState(0);

  const fetchBalanceSummary = useCallback(async () => {
    const result = await getBalanceSummary();
    if (result.success) {
      setBalanceSummary(result.data);
    } else {
      reportError(result.error, 'EarningsPayout balance summary');
    }
    return result;
  }, []);

  useEffect(() => {
    const loadBalanceSummary = async () => {
      setLoading(true);
      await fetchBalanceSummary();
      setLoading(false);
    };

    loadBalanceSummary();
  }, [fetchBalanceSummary]);

  const handlePayoutSuccess = useCallback(async () => {
    await fetchBalanceSummary();
    setPayoutDataRefreshKey((key) => key + 1);
  }, [fetchBalanceSummary]);

  // Fetch W-8BEN form status for non-US users (required for cash out)
  useEffect(() => {
    if (isUsCountry(user?.country)) return;

    let isActive = true;

    const fetchW8Status = async () => {
      setW8StatusLoading(true);
      try {
        const response = await getW8Form();
        if (!isActive) return;

        if (response?.success && response?.data) {
          const formData = response.data?.data ?? response.data;
          const hasFormData =
            formData && typeof formData === 'object' && Object.keys(formData).length > 0;

          setW8FormFilled(Boolean(hasFormData));
          setW8FormExpired(hasFormData ? isW8FormExpired(formData) : false);
          setW8PendingSigningUrl(hasFormData ? getPendingSigningUrl(formData?.taxBanditsResponse) : null);
        } else {
          setW8FormFilled(false);
          setW8FormExpired(false);
          setW8PendingSigningUrl(null);
        }
      } catch {
        if (isActive) {
          setW8FormFilled(false);
          setW8FormExpired(false);
          setW8PendingSigningUrl(null);
        }
      } finally {
        if (isActive) setW8StatusLoading(false);
      }
    };

    fetchW8Status();
    return () => {
      isActive = false;
    };
  }, [user?.country]);

  // Fetch W-9 form status for US users (required for cash out: must be filled and signed)
  useEffect(() => {
    if (!isUsCountry(user?.country)) return;

    let isActive = true;

    const fetchW9Status = async () => {
      setW9StatusLoading(true);
      try {
        const response = await getW9Form();
        if (!isActive) return;

        if (!response?.success || !response?.data) {
          setW9PendingSigningUrl(null);
          setW9FormFilled(false);
          return;
        }

        const formData = response?.data?.data;
        const url = getPendingSigningUrl(formData?.taxBanditsResponse);
        setW9PendingSigningUrl(url);
        setW9FormFilled(Boolean(formData && typeof formData === 'object' && Object.keys(formData).length > 0 && !url));
      } catch {
        if (isActive) {
          setW9PendingSigningUrl(null);
          setW9FormFilled(false);
        }
      } finally {
        if (isActive) setW9StatusLoading(false);
      }
    };

    fetchW9Status();
    return () => {
      isActive = false;
    };
  }, [user?.country]);

  const isUsUser = isUsCountry(user?.country);
  const totalEarnings =
    balanceSummary != null
      ? (Number(balanceSummary.available) || 0) +
        (Number(balanceSummary.pending) || 0) +
        (Number(balanceSummary.currentYearPayout) || 0)
      : null;
  const isUsEarningsAtOrAboveW9Threshold = totalEarnings != null && totalEarnings >= CONFIG.MIN_BALANCE_TO_TAX_INFO;
  const isTaxInfoDisabled = isUsUser ? !isUsEarningsAtOrAboveW9Threshold : false;
  const showTaxInfoRow = shouldShowTaxInfoInAccountSetup({
    isUsUser,
    w9FormFilled,
    w8FormFilled,
    w8FormExpired,
    w8PendingSigningUrl,
  });

  const countryCode = user?.country;
  const isVatApplicable = useMemo(() => {
    return getVatRateByCountryCode.find(
      (vatRate) => vatRate.code === countryCode
    );
  }, [countryCode]);
  // const isGstApplicable = useMemo(() => {
  //   return getGstRateByCountryCode.find(
  //     (gstRate) => gstRate.code === countryCode
  //   );
  // }, [countryCode]);
  const isGstApplicable = false;

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="Earnings & Payout"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
        backButtonUrl={ROUTES.MY_PROFILE}
      />

      {user && user?._id && (
        (isUsUser && !isTaxInfoDisabled && !w9FormFilled && !w9PendingSigningUrl) ||
        (!isUsUser && (!w8FormFilled || w8FormExpired) && !w8PendingSigningUrl) ? (
          <AddTaxInfoCard
            isUsUser={isUsUser}
            w8FormFilled={w8FormFilled}
            w8FormExpired={w8FormExpired}
            w8StatusLoading={w8StatusLoading}
          />
        ) : null
      )}

      {(w8PendingSigningUrl || w9PendingSigningUrl) && (
        <TaxBanditsSignatureRequiredCard
          pendingSigningUrl={isUsUser ? w9PendingSigningUrl : w8PendingSigningUrl}
          isUsUser={isUsUser}
        />
      )}

      <BalanceOverviewCard
        user={user}
        balanceSummary={balanceSummary}
        loading={loading}
        isUsUser={isUsUser}
        w8FormFilled={w8FormFilled}
        w8FormExpired={w8FormExpired}
        w8PendingSigningUrl={w8PendingSigningUrl}
        w8StatusLoading={w8StatusLoading}
        w9PendingSigningUrl={w9PendingSigningUrl}
        w9FormFilled={w9FormFilled}
        w9StatusLoading={w9StatusLoading}
        totalEarnings={totalEarnings}
        onPayoutSuccess={handlePayoutSuccess}
      />

      <TotalEarningsTodayCard
        user={user}
      />

      <EarningsBreakdownCard
        user={user}
      />

      <MonthlyEarningsCard
        user={user}
        isVatApplicable={isVatApplicable}
        isGstApplicable={isGstApplicable}
      />

      <DailyEarningsCard
        user={user}
        isVatApplicable={isVatApplicable}
        isGstApplicable={isGstApplicable}
      />

      <PayoutHistoryCard refreshKey={payoutDataRefreshKey} />

      <AccountSetupCard
        isVatApplicable={isVatApplicable}
        isGstApplicable={isGstApplicable}
        isTaxInfoDisabled={isTaxInfoDisabled}
        isUsUser={isUsUser}
        showTaxInfoRow={showTaxInfoRow}
        w8FormExpired={w8FormExpired}
        pendingSigningUrl={isUsUser ? w9PendingSigningUrl : w8PendingSigningUrl}
      />

      <ExportDataCard
        user={user}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(EarningsPayout);
